import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type NamedDocument, useUpdateDocument } from '@/models/document'

export function RenameDocumentDialog({
  orgId,
  document,
  onClose,
}: {
  orgId: string
  document: NamedDocument
  onClose: () => void
}) {
  const [name, setName] = useState(document.displayName)
  const updateDocument = useUpdateDocument(orgId)

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    updateDocument.mutate(
      { id: document.id, name: trimmed },
      {
        onSuccess: onClose,
        onError: () => toast.error('Could not rename that document.'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename document</DialogTitle>
          <DialogDescription>
            This replaces the name we read off the file. Clearing it falls back to the parsed name.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="vault-document-name">Name</Label>
          <Input
            id="vault-document-name"
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={updateDocument.isPending ? 'Saving…' : undefined}
            disabled={!name.trim()}
            onClick={submit}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
